import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineHealthAndSafety,
  MdOutlineSubtitles,
  MdPeopleOutline,
} from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { useFieldError } from "shared/hooks/useFieldRegistry";
import { NAME_SPACES } from "shared/locales/constants";
import {
  AUTHORITY_TYPE,
  DATE_FORMAT,
  GENDER,
  MILTIS_SOCIAL_REGIMES,
  NIR_INHERITANCE_FROM,
  RELATIONSHIP,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";
import {
  childNirIsManualEntry,
  childRequiresOffContractSpouseNir,
  getNirInheritanceFromStored,
  resolveChildInheritedNir,
} from "utils/helpers/miltisRelatedPersonChild";

const isPendingParentNir = ({
  authority,
  nirInheritanceFrom,
  adherentNir,
  spouseNir,
  hasSpouse,
}) => {
  if (childNirIsManualEntry(authority, hasSpouse, nirInheritanceFrom)) {
    return false;
  }
  const adherent = (adherentNir || "").trim();
  const spouse = (spouseNir || "").trim();
  if (authority === AUTHORITY_TYPE.FATHER) return !adherent;
  if (authority === AUTHORITY_TYPE.MOTHER) return hasSpouse && !spouse;
  if (authority === AUTHORITY_TYPE.BOTH) {
    if (nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE)
      return !hasSpouse || !spouse;
    return !adherent;
  }
  return false;
};

const RegimeMismatchAlert = ({
  authorityPath,
  socialRegimePath,
  authority,
}) => {
  const authorityError = useFieldError(authorityPath);
  const socialRegimeError = useFieldError(socialRegimePath);
  const message = socialRegimeError || authorityError;
  if (!message || !authority || authority === AUTHORITY_TYPE.SELF) {
    return null;
  }
  return (
    <Col lg={24} xs={24}>
      <Alert showIcon type="error" message={message} />
    </Col>
  );
};

const Children = ({
  setPercent,
  isFieldRequired,
  bindField,
  initialValues,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });
  const { t: p } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const SOCIAL_REGIMES_TRANSLATION = p("B2C_MILTIS.SOCIAL_REGIMES", {
    returnObjects: true,
  });
  const FC = SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN;

  const hasSpouse = !!get(
    initialValues,
    "project.contact.relatedPersons",
    [],
  ).find((person) => person.relationship === RELATIONSHIP.SPOUSE);

  const childrenSignature = JSON.stringify(
    get(initialValues, "children", []).map((c) => ({
      a: c.authority,
      n: getNirInheritanceFromStored(c),
    })),
  );

  const adherentNirSynced = get(
    initialValues,
    "project.contact.socialSecurityNumber",
  );
  const spouseNirSynced = get(
    initialValues,
    "spouse.identity.socialSecurityNumber",
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when parent NIRs or attachment change
  useEffect(() => {
    const adherentNir = adherentNirSynced;
    const spouseNir = spouseNirSynced;
    const childrenList = get(initialValues, "children", []);
    let changed = false;
    childrenList.forEach((child, index) => {
      if (child.authority == null) return;
      const nirInheritanceFrom = getNirInheritanceFromStored(child);
      if (
        childNirIsManualEntry(child.authority, hasSpouse, nirInheritanceFrom)
      ) {
        if (
          childRequiresOffContractSpouseNir(
            child.authority,
            hasSpouse,
            nirInheritanceFrom,
          )
        ) {
          const cur =
            get(
              initialValues,
              `children.${index}.identity.socialSecurityNumber`,
            ) ?? "";
          const adherent = (adherentNir || "").trim();
          if (cur && cur === adherent) {
            set(
              initialValues,
              `children.${index}.identity.socialSecurityNumber`,
              "",
            );
            changed = true;
          }
        }
        return;
      }
      const next = resolveChildInheritedNir({
        authority: child.authority,
        nirInheritanceFrom,
        adherentNir,
        spouseNir,
        hasSpouse,
      });
      const cur =
        get(initialValues, `children.${index}.identity.socialSecurityNumber`) ??
        "";
      const normalizedNext = next ?? "";
      if (cur !== normalizedNext) {
        set(
          initialValues,
          `children.${index}.identity.socialSecurityNumber`,
          normalizedNext,
        );
        changed = true;
      }
    });
    if (changed) setPercent(initialValues);
  }, [
    adherentNirSynced,
    spouseNirSynced,
    childrenSignature,
    hasSpouse,
    initialValues,
    setPercent,
  ]);

  const pendingParentLabel = (authority, nirInheritanceFrom) => {
    if (authority === AUTHORITY_TYPE.FATHER) return FC.PARENT_NAME_ADHERENT;
    if (authority === AUTHORITY_TYPE.MOTHER) return FC.PARENT_NAME_SPOUSE;
    if (authority === AUTHORITY_TYPE.BOTH) {
      return nirInheritanceFrom === NIR_INHERITANCE_FROM.SPOUSE
        ? FC.PARENT_NAME_SPOUSE
        : FC.PARENT_NAME_ADHERENT;
    }
    return "";
  };

  return (
    get(initialValues, "project.contact.relatedPersons", []).find(
      (person) => person.relationship === RELATIONSHIP.CHILD,
    ) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CHILDREN}-section`}
      >
        <Form type="vertical" title={FC.TITLE} subtitle={FC.SUBTITLE}>
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert showIcon type="info" message={FC.INFO} />
            </Col>
            {get(initialValues, "children", []).map((_, index) => {
              const authority = get(
                initialValues,
                `children.${index}.authority`,
              );
              const nirInheritanceFrom = getNirInheritanceFromStored(
                get(initialValues, `children.${index}`),
              );
              const adherentNir = get(
                initialValues,
                "project.contact.socialSecurityNumber",
              );
              const spouseNir = get(
                initialValues,
                "spouse.identity.socialSecurityNumber",
              );
              const showPending = isPendingParentNir({
                authority,
                nirInheritanceFrom,
                adherentNir,
                spouseNir,
                hasSpouse,
              });
              const ssnLocked = !childNirIsManualEntry(
                authority,
                hasSpouse,
                nirInheritanceFrom,
              );
              const childSocialSecurityNumber =
                get(
                  initialValues,
                  `children.${index}.identity.socialSecurityNumber`,
                ) ?? "";

              const genderPath = `children.${index}.identity.user.gender`;
              const genderField = bindField(genderPath, {
                label: SUBSCRIPTION.FORM.GENDER.LABEL,
                required: isFieldRequired(genderPath),
                onChange: () => setPercent(initialValues),
              });

              const firstnamePath = `children.${index}.identity.user.firstname`;
              const firstnameField = bindField(firstnamePath, {
                label: SUBSCRIPTION.FORM.FIRST_NAME.LABEL,
                required: isFieldRequired(firstnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const lastnamePath = `children.${index}.identity.user.lastname`;
              const lastnameField = bindField(lastnamePath, {
                label: SUBSCRIPTION.FORM.LAST_NAME.LABEL,
                required: isFieldRequired(lastnamePath),
                onBlur: () => setPercent(initialValues),
              });

              const authorityPath = `children.${index}.authority`;
              const authorityField = bindField(authorityPath, {
                label: FC.AUTHORITY.LABEL,
                required: isFieldRequired(authorityPath),
                onChange: (value) => {
                  const baseFields =
                    get(
                      initialValues,
                      `children.${index}.identity.fields`,
                      {},
                    ) || {};
                  if (value !== AUTHORITY_TYPE.BOTH) {
                    const next = { ...baseFields };
                    delete next.nirInheritanceFrom;
                    set(
                      initialValues,
                      `children.${index}.identity.fields`,
                      next,
                    );
                  } else {
                    set(initialValues, `children.${index}.identity.fields`, {
                      ...baseFields,
                      nirInheritanceFrom: NIR_INHERITANCE_FROM.ADHERENT,
                    });
                  }
                  if (value === AUTHORITY_TYPE.SELF) {
                    set(
                      initialValues,
                      `children.${index}.identity.socialSecurityNumber`,
                      "",
                    );
                  } else if (
                    childRequiresOffContractSpouseNir(value, hasSpouse)
                  ) {
                    set(
                      initialValues,
                      `children.${index}.identity.socialSecurityNumber`,
                      "",
                    );
                  }
                  setPercent(initialValues);
                },
              });

              const nirInheritanceFromPath = `children.${index}.identity.fields.nirInheritanceFrom`;
              const nirInheritanceFromField = bindField(
                nirInheritanceFromPath,
                {
                  label: FC.NIR_INHERITANCE_FROM.LABEL,
                  required: true,
                  onChange: (value) => {
                    const baseFields =
                      get(
                        initialValues,
                        `children.${index}.identity.fields`,
                        {},
                      ) || {};
                    set(initialValues, `children.${index}.identity.fields`, {
                      ...baseFields,
                      nirInheritanceFrom: value,
                    });
                    if (
                      childRequiresOffContractSpouseNir(
                        AUTHORITY_TYPE.BOTH,
                        hasSpouse,
                        value,
                      )
                    ) {
                      set(
                        initialValues,
                        `children.${index}.identity.socialSecurityNumber`,
                        "",
                      );
                    }
                    setPercent(initialValues);
                  },
                },
              );

              const socialRegimePath = `children.${index}.identity.socialRegime`;
              const socialRegimeField = bindField(socialRegimePath, {
                label: SUBSCRIPTION.FORM.MANDATORY_SCHEME.LABEL,
                required: isFieldRequired(socialRegimePath),
                onChange: () => setPercent(initialValues),
              });

              const socialSecurityNumberPath = `children.${index}.identity.socialSecurityNumber`;
              const socialSecurityNumberField = bindField(
                socialSecurityNumberPath,
                {
                  label: SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER.LABEL,
                  required: isFieldRequired(socialSecurityNumberPath),
                  disabled: ssnLocked,
                  onBlur: () => setPercent(initialValues),
                },
              );

              return (
                <Col lg={24} xs={24} key={`child-form-${index}`}>
                  <Form
                    type="highlight"
                    title={`${FC.CHILD.TITLE} ${index + 1}`}
                  >
                    <Row gutter={[16, 16]}>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={genderField.path}
                          label={genderField.label}
                          required={genderField.required}
                          disabled={genderField.disabled}
                        >
                          {(field) => (
                            <Input
                              type="radio"
                              variant="group"
                              icon={<MdPeopleOutline icon="man" size={16} />}
                              values={[
                                {
                                  value: GENDER.MALE,
                                  label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MR,
                                },
                                {
                                  value: GENDER.FEMALE,
                                  label: SUBSCRIPTION.FORM.GENDER.OPTIONS.MRS,
                                },
                              ]}
                              label={genderField.label}
                              value={get(initialValues, genderPath)}
                              {...field}
                              onChange={genderField.onChange}
                              onBlur={genderField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={firstnameField.path}
                          label={firstnameField.label}
                          required={firstnameField.required}
                          disabled={firstnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              placeholder={
                                SUBSCRIPTION.FORM.FIRST_NAME.PLACEHOLDER
                              }
                              label={firstnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              type="custom"
                              normalizeText={true}
                              defaultValue={firstnameField.defaultValue}
                              {...field}
                              onChange={firstnameField.onChange}
                              onBlur={firstnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={lastnameField.path}
                          label={lastnameField.label}
                          required={lastnameField.required}
                          disabled={lastnameField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={lastnameField.label}
                              icon={<MdOutlineBadge size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.LAST_NAME.PLACEHOLDER
                              }
                              type="custom"
                              normalizeText={true}
                              defaultValue={lastnameField.defaultValue}
                              {...field}
                              onChange={lastnameField.onChange}
                              onBlur={lastnameField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <Input
                          label={SUBSCRIPTION.FORM.BIRTH_DATE.LABEL}
                          icon={<MdOutlineCake size={16} />}
                          placeholder={SUBSCRIPTION.FORM.BIRTH_DATE.PLACEHOLDER}
                          type="date"
                          variant="simple"
                          defaultValue={dayjs(
                            get(
                              initialValues,
                              `children.${index}.identity.user.birthDate`,
                            ),
                          )}
                          format={DATE_FORMAT}
                          disabled
                          required={isFieldRequired(
                            `children.${index}.identity.user.birthDate`,
                          )}
                        />
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={authorityField.path}
                          label={authorityField.label}
                          required={authorityField.required}
                          disabled={authorityField.disabled}
                        >
                          {(field) => (
                            <Input
                              type="radio"
                              variant="group"
                              icon={<MdPeopleOutline size={16} />}
                              values={[
                                {
                                  value: AUTHORITY_TYPE.FATHER,
                                  label: FC.AUTHORITY.FATHER,
                                },
                                {
                                  value: AUTHORITY_TYPE.MOTHER,
                                  label: FC.AUTHORITY.MOTHER,
                                },
                                {
                                  value: AUTHORITY_TYPE.SELF,
                                  label: FC.AUTHORITY.SELF,
                                },
                              ]}
                              label={authorityField.label}
                              value={authority}
                              {...field}
                              onChange={authorityField.onChange}
                              onBlur={authorityField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      {authority === AUTHORITY_TYPE.BOTH && (
                        <Col lg={24} xs={24}>
                          <FormField
                            path={nirInheritanceFromField.path}
                            label={nirInheritanceFromField.label}
                            required={nirInheritanceFromField.required}
                            disabled={nirInheritanceFromField.disabled}
                          >
                            {(field) => (
                              <Select
                                label={nirInheritanceFromField.label}
                                icon={<MdOutlineHealthAndSafety size={16} />}
                                placeholder={FC.NIR_INHERITANCE_FROM.LABEL}
                                type="simple"
                                options={[
                                  {
                                    label: FC.NIR_INHERITANCE_FROM.ADHERENT,
                                    value: NIR_INHERITANCE_FROM.ADHERENT,
                                  },
                                  {
                                    label: FC.NIR_INHERITANCE_FROM.SPOUSE,
                                    value: NIR_INHERITANCE_FROM.SPOUSE,
                                  },
                                ]}
                                value={nirInheritanceFrom}
                                {...field}
                                onChange={nirInheritanceFromField.onChange}
                                onBlur={nirInheritanceFromField.onBlur}
                              />
                            )}
                          </FormField>
                        </Col>
                      )}
                      {showPending && (
                        <Col lg={24} xs={24}>
                          <Alert
                            showIcon
                            type="info"
                            message={t(
                              "SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CHILDREN.PARENT_NIR_PENDING",
                              {
                                parent: pendingParentLabel(
                                  authority,
                                  nirInheritanceFrom,
                                ),
                              },
                            )}
                          />
                        </Col>
                      )}
                      <RegimeMismatchAlert
                        authorityPath={authorityPath}
                        socialRegimePath={socialRegimePath}
                        authority={authority}
                      />
                      <Col lg={24} xs={24}>
                        <FormField
                          path={socialRegimeField.path}
                          label={socialRegimeField.label}
                          required={socialRegimeField.required}
                          disabled={socialRegimeField.disabled}
                        >
                          {(field) => (
                            <Select
                              label={socialRegimeField.label}
                              icon={<MdOutlineHealthAndSafety size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.MANDATORY_SCHEME.PLACEHOLDER
                              }
                              type="simple"
                              options={Object.values(MILTIS_SOCIAL_REGIMES).map(
                                (option) => {
                                  const key = Object.keys(
                                    MILTIS_SOCIAL_REGIMES,
                                  ).find(
                                    (k) => MILTIS_SOCIAL_REGIMES[k] === option,
                                  );
                                  return {
                                    label:
                                      SOCIAL_REGIMES_TRANSLATION[key] || option,
                                    value: option,
                                  };
                                },
                              )}
                              defaultValue={socialRegimeField.defaultValue}
                              {...field}
                              onChange={socialRegimeField.onChange}
                              onBlur={socialRegimeField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                      <Col lg={24} xs={24}>
                        <FormField
                          path={socialSecurityNumberField.path}
                          label={socialSecurityNumberField.label}
                          required={socialSecurityNumberField.required}
                          disabled={socialSecurityNumberField.disabled}
                        >
                          {(field) => (
                            <Input
                              label={socialSecurityNumberField.label}
                              icon={<MdOutlineSubtitles size={16} />}
                              placeholder={
                                SUBSCRIPTION.FORM.SOCIAL_SECURITY_NUMBER
                                  .PLACEHOLDER
                              }
                              type="custom"
                              trim={true}
                              key={`child-ssn-${index}-${authority}-${ssnLocked}`}
                              {...(ssnLocked
                                ? { value: childSocialSecurityNumber }
                                : {
                                    defaultValue: childSocialSecurityNumber,
                                  })}
                              {...field}
                              onChange={socialSecurityNumberField.onChange}
                              onBlur={socialSecurityNumberField.onBlur}
                            />
                          )}
                        </FormField>
                      </Col>
                    </Row>
                  </Form>
                </Col>
              );
            })}
          </Row>
        </Form>
      </div>
    )
  );
};

export default Children;
